import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { message, Steps, theme } from 'antd';
import { RootState } from 'store/configureStore';
import {
  setSelectedTPP,
  set_isTppUpdated,
} from 'store/ducks/DataEntry/ThirdPartyProviders/tppSlice';
import {
  setSelectedContract_ICT,
  setSelectedTPP_ICT,
  setSelecedEntity_ICT,
  setSelectedTPP_Multi,
} from 'store/ducks/DataEntry/ICTServices/ICTServiceSlice';
import {
  setSelecedEntity_Contractual_Information,
  setSelectedTPP_Contractual_Information,
} from 'store/ducks/DataEntry/ContractualInformation/contractualInformationSlice';
import { setSelectedSystem } from 'store/ducks/DataManagement/Article8AndICTSystem/article8Slice';
import TitleBar from 'components/TitleBar/TitleBar';
import useFilters from 'utils/hooks/useFilters';
import { tppOp } from 'store/ducks/DataEntry/ThirdPartyProviders';
import { ict_serviceOp } from 'store/ducks/DataEntry/ICTServices';
import { ISelectedValueContract } from 'store/ducks/DataEntry/ICTServices/types';
import { entityOp } from 'store/ducks/DataEntry/Entities';
import {
  set_idOfSelectedFinancialEntity,
  set_isSubmitionTrue,
} from 'store/ducks/DataEntry/Entities/entitySlice';
import { set_selectedOption99 } from 'store/ducks/Global/globalSlice';
import { setContractRefNumber } from 'store/ducks/DataEntry/ICTServices/ICTServiceSlice';
import { STEP_KEYS, StepComponentProps } from 'store/ducks/Global/types';
import { options99 } from 'constants/options99';
import { stepPathMap } from 'constants/stepPaths';
import * as Styled from 'components/Layout/Layout.styled';

const { useToken } = theme;

const StepComponent: React.FC<StepComponentProps> = ({ steps, footer }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { token } = useToken();
  const { t } = useTranslation();
  const [current, setCurrent] = useState(
    () => stepPathMap[location.pathname] ?? 0,
  );
  const [selectedTPPForICT, setSelectedTPPForICT] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [selectedTPPsMulti, setSelectedTPPsMulti] = useState<string[]>([]);
  const [selectedFunctionForICT, setSelectedFunctionForICT] = useState<
    { value: string; label: string } | undefined
  >(undefined);
  const [selectedContractForICT, setSelectedContractForICT] = useState<
    string | undefined
  >(undefined);
  const [
    selectedBranchForContractualInformation,
    setSelectedBranchForContractualInformation,
  ] = useState<{ value: string; label: string } | undefined>(undefined);
  const [
    selectedICTForContractualInformation,
    setSelectedICTForContractualInformation,
  ] = useState<string | undefined>(undefined);
  const [tppdData, setTPPData] = useState<any[]>([]);
  const [entityMaintaining, setEntityMaintaining] = useState<any[]>([]);
  const [contractsData, setContractsData] = useState<any[]>([]);
  const [contractName, setContractName] = useState<any[]>([]);
  const { query, handleFilterChange, extractedOptions } = useFilters();
  const { scheme, selectedOption99 } = useSelector(
    (state: RootState) => state.global,
  );
  const { selectedSystem } = useSelector((state: RootState) => state.article8);
  const { idOfSelectedFinancialEntity, isSubmitionTrue } = useSelector(
    (state: RootState) => state.entities,
  );
  const [selectedOptionReport99, setSelectedOptionReport99] = useState(null);

  const { selectedTPP, isTppUpdated } = useSelector(
    (state: RootState) => state.tpp,
  );

  const handleChangeReport99 = (option: React.SetStateAction<null>) => {
    setSelectedOptionReport99(option);
    dispatch(set_selectedOption99(option));
  };

  const onStepChange = (stepIndex: number) => setCurrent(stepIndex);

  const handleSelectSystem = (value: string) => {
    dispatch(setSelectedSystem(value));
  };

  const handleTPPMultiChange = (
    values: string[],
    options: { value: string; label: string }[],
  ) => {
    setSelectedTPPsMulti(values);

    const mapped = options.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }));

    dispatch(setSelectedTPP_Multi(mapped));
  };

  useEffect(() => {
    if (
      steps[current]?.key !== STEP_KEYS.ICT_PROVIDER &&
      selectedTPP === 'All'
    ) {
      dispatch(setSelectedTPP(''));
      message.info('Please select a valid TPP.');
    }
  }, [current, steps]);

  const fetchEntityMaintaining = async () => {
    try {
      const response = await entityOp.fetchAllFinancialEntities();
      const data = response?.data;
      if (data.length > 0) {
        const updatedTPPData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          financial_entity_name: item.financial_entity_name,
        }));
        setEntityMaintaining(updatedTPPData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTPPbyEntity = async () => {
    try {
      const response = await tppOp.fetchTPPbyEntityName({
        entity_id: query.entityId,
        entity_name: query.entity,
      });
      const data = response?.data.third_party_service_providers;
      if (data.length > 0) {
        const updatedTPPData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          third_party_service_provider_name:
            item.third_party_service_provider_name,
        }));
        setTPPData(updatedTPPData);
      } else {
        setTPPData([]);
      }
      setSelectedTPPForICT(undefined);
      dispatch(setSelectedTPP(''));
    } catch (error) {
      console.log(error, 'error');
      setTPPData([]);
    }
  };

  const fetchContractsByEntity = async () => {
    try {
      const response =
        await ict_serviceOp.fetchContractualArrangementsbyEntityName({
          entity_id: query.entityId,
          entity_name: query.entity,
        });
      const data = response?.data;

      if (data.length > 0) {
        const updatedContractsData = data.map((item: any, index: number) => ({
          key: index,
          id: item.id,
          description: item.description,
          arrangement_reference_number: item.arrangement_reference_number,
          contract_name: item.contract_name,
        }));

        setContractsData(updatedContractsData);
      } else {
        setContractsData([]);
      }
    } catch (error) {
      console.log(error, 'error');
      setContractsData([]);
    }
  };

  useEffect(() => {
    fetchEntityMaintaining();
  }, []);

  useEffect(() => {
    const contract = contractsData.find((c) => c.id === selectedContractForICT);

    if (contract) {
      setContractName(contract.contract_name);
      dispatch(setContractRefNumber(contract.arrangement_reference_number));
    }
  }, [selectedContractForICT]);

  useEffect(() => {
    if (isSubmitionTrue) {
      fetchEntityMaintaining();
      dispatch(set_isSubmitionTrue(false));
    }
  }, [isSubmitionTrue]);

  useEffect(() => {
    if (query.entity) {
      fetchTPPbyEntity();
      fetchContractsByEntity();
    }
  }, [query.entity]);

  useEffect(() => {
    if (isTppUpdated) {
      fetchTPPbyEntity();
      dispatch(set_isTppUpdated(false));
    }
  }, [isTppUpdated]);

  const handleTPPForICTChange = (
    value: string | undefined,
    option?: { label: string },
  ) => {
    if (value === undefined) {
      setSelectedTPPForICT(undefined);
      dispatch(setSelectedTPP(''));
      return;
    }

    if (!option) return;
    setSelectedTPPForICT({ value, label: option.label });
    dispatch(setSelectedTPP(value));

    if (value) {
      const selectedTPP = {
        value: value,
        label: option.label,
      };
      dispatch(setSelectedTPP_ICT(selectedTPP));
      dispatch(setSelectedTPP_Contractual_Information(selectedTPP));
      setSelectedFunctionForICT(undefined);
      setSelectedContractForICT(undefined);
      setSelectedBranchForContractualInformation(undefined);
      setSelectedICTForContractualInformation(undefined);
    }
  };

  const handleChangeFinancialEntity = (selectedOption: any) => {
    dispatch(set_idOfSelectedFinancialEntity(selectedOption));
  };

  const handleChange = (selectedLabel: string | undefined) => {
    if (!selectedLabel) {
      handleFilterChange('', '');
      setSelectedTPPForICT(undefined);
      setSelectedFunctionForICT(undefined);
      setSelectedBranchForContractualInformation(undefined);
      setSelectedContractForICT(undefined);
      setSelectedICTForContractualInformation(undefined);
      return;
    }

    const selectedOption = extractedOptions.find(
      (option: { label: string }) => option.label === selectedLabel,
    );
    if (selectedOption) {
      const selectedEntity = {
        entityId: selectedOption.key,
        entity: selectedOption.label,
      };
      dispatch(setSelecedEntity_ICT(selectedEntity));

      dispatch(setSelecedEntity_Contractual_Information(selectedEntity));

      handleFilterChange(selectedOption.label, selectedOption.key);
      setSelectedTPPForICT(undefined);
      setSelectedFunctionForICT(undefined);
      setSelectedBranchForContractualInformation(undefined);
      setSelectedContractForICT(undefined);
      setSelectedICTForContractualInformation(undefined);
    }
  };

  const handleContractForICTChange = (value: string | undefined) => {
    setSelectedContractForICT(value);

    if (value) {
      const contractPayload: ISelectedValueContract = { value };
      dispatch(setSelectedContract_ICT(contractPayload));
    } else {
      dispatch(setSelectedContract_ICT(null));
    }

    setSelectedTPPForICT(undefined);
    setSelectedFunctionForICT(undefined);
  };

  const stepsWithoutFilters = [
    'Role type',
    'Risk Exposure',
    'Dependency type',
    'ICT System criticality',
  ];

  type StepKey = keyof typeof STEP_KEYS;
  const HIDDEN_FILTER_STEPS: StepKey[] = [
    STEP_KEYS.ICT_SERVICE,
    STEP_KEYS.CONTRACTUAL_INFORMATION,
    STEP_KEYS.ENTITY_MAINTAINING_REGISTER,
    STEP_KEYS.DATA,
  ];
  const safeCurrent = current < steps.length ? current : 0;
  const currentKey = steps[safeCurrent]?.key;

  const hideEntityFilter =
    !!currentKey && !HIDDEN_FILTER_STEPS.includes(currentKey);

  const hideFilters = stepsWithoutFilters.includes(steps[safeCurrent]?.title);

  return (
    <Styled.RootLayout>
      <TitleBar title={steps[safeCurrent]?.title} />
      <Styled.ContentArea>
        <Styled.HorizontalLayout theme={scheme}>
          <Styled.StepsComponent token={token}>
            <Steps
              current={current}
              items={steps.map((step) => ({ title: step.title }))}
              onChange={onStepChange}
            />
          </Styled.StepsComponent>
        </Styled.HorizontalLayout>
      </Styled.ContentArea>
      {!hideFilters && (
        <Styled.FilterCard>
          <Styled.FilterGrid>
            {/* DEFAULT ENTITY FILTER */}
            {hideEntityFilter && (
              <Styled.FilterItem>
                <Styled.FilterLabel>{t('AREAS.ENTITY')}</Styled.FilterLabel>

                <Styled.SelectFilter
                  allowClear
                  showSearch
                  placeholder={t('GENERAL.SELECT_ENTITY')}
                  options={extractedOptions.map((option) => ({
                    value: option.label,
                    key: option.key,
                    label: option.label,
                  }))}
                  value={query.entity}
                  onChange={handleChange}
                  filterOption={(input: string, option: { label: string }) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Styled.FilterItem>
            )}

            {/* ICT THIRD PARTY PROVIDER */}
            {steps[safeCurrent]?.key === STEP_KEYS.ICT_PROVIDER && (
              <Styled.FilterItem>
                <Styled.FilterLabel>
                  {t('GENERAL.SELECT_TPP')}
                </Styled.FilterLabel>
                <Styled.SelectFilter
                  mode="multiple"
                  allowClear
                  showSearch
                  options={tppdData
                    ?.filter(
                      (item) =>
                        item?.id && item?.third_party_service_provider_name,
                    )
                    .map((item) => ({
                      value: item.id.toString(),
                      label: item.third_party_service_provider_name,
                    }))}
                  value={selectedTPPsMulti}
                  onChange={handleTPPMultiChange}
                  filterOption={(input: string, option: { label: string }) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Styled.FilterItem>
            )}
            {steps[safeCurrent]?.key === STEP_KEYS.CURRENCY_AND_COSTS && (
              <Styled.FilterItem>
                <Styled.FilterLabel>
                  {t('AREAS.ICT_THIRD_PARTY_SERVICE_PROVIDER')}
                </Styled.FilterLabel>

                <Styled.SelectFilter
                  allowClear
                  showSearch
                  options={tppdData
                    ?.filter(
                      (item) =>
                        item?.id && item?.third_party_service_provider_name,
                    )
                    .map((item) => ({
                      value: item.id,
                      label: item.third_party_service_provider_name,
                      key: item.key,
                    }))}
                  onChange={handleTPPForICTChange}
                  value={selectedTPPForICT}
                  filterOption={(input: string, option: { label: string }) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Styled.FilterItem>
            )}
            {/* ENTITY MAINTAINING REGISTER */}
            {steps[safeCurrent]?.key ===
              STEP_KEYS.ENTITY_MAINTAINING_REGISTER && (
              <Styled.FilterItem>
                <Styled.FilterLabel>
                  {t('STEPS.ENTITY_MAINTAINING_REGISTER')}
                </Styled.FilterLabel>
                <Styled.SelectFilter
                  allowClear
                  placeholder={t('GENERAL.SELECT_ENTITY')}
                  options={entityMaintaining.map((option) => ({
                    value: option.id,
                    key: option.key,
                    label: option.financial_entity_name,
                  }))}
                  value={idOfSelectedFinancialEntity}
                  onChange={handleChangeFinancialEntity}
                  filterOption={(input: string, option: { label: string }) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Styled.FilterItem>
            )}

            {/* ICT SERVICE */}
            {steps[safeCurrent]?.key === STEP_KEYS.ICT_SERVICE && (
              <>
                <Styled.FilterItem>
                  <Styled.FilterLabel>{t('AREAS.ENTITY')}</Styled.FilterLabel>
                  <Styled.SelectFilter
                    allowClear
                    showSearch
                    placeholder={t('GENERAL.SELECT_ENTITY')}
                    options={extractedOptions.map((option) => ({
                      value: option.label,
                      key: option.key,
                      label: option.label,
                    }))}
                    value={query.entity}
                    onChange={handleChange}
                    filterOption={(input: string, option: { label: string }) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Styled.FilterItem>

                <Styled.FilterItem>
                  <Styled.FilterLabel>
                    {t('AREAS.CONTRACTUAL_INFORMATION')}
                  </Styled.FilterLabel>
                  <Styled.SelectFilter
                    allowClear
                    showSearch
                    options={contractsData.map((item) => ({
                      value: item.id,
                      label: item.contract_name || item.description || item.id,
                      key: item.key,
                    }))}
                    onChange={handleContractForICTChange}
                    value={selectedContractForICT}
                    filterOption={(input: string, option: { label: string }) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Styled.FilterItem>
              </>
            )}

            {/* CONTRACTUAL INFORMATION */}
            {steps[safeCurrent]?.key === STEP_KEYS.CONTRACTUAL_INFORMATION && (
              <>
                <Styled.FilterItem>
                  <Styled.FilterLabel>{t('AREAS.ENTITY')}</Styled.FilterLabel>
                  <Styled.SelectFilter
                    allowClear
                    showSearch
                    placeholder={t('GENERAL.SELECT_ENTITY')}
                    options={extractedOptions.map((option) => ({
                      value: option.label,
                      key: option.key,
                      label: option.label,
                    }))}
                    value={query.entity}
                    onChange={handleChange}
                    filterOption={(input: string, option: { label: string }) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Styled.FilterItem>

                <Styled.FilterItem>
                  <Styled.FilterLabel>
                    {t('AREAS.ICT_THIRD_PARTY_SERVICE_PROVIDER')}
                  </Styled.FilterLabel>
                  <Styled.SelectFilter
                    allowClear
                    showSearch
                    options={tppdData.map((item) => ({
                      value: item.id,
                      label: item.third_party_service_provider_name,
                      key: item.key,
                    }))}
                    onChange={handleTPPForICTChange}
                    value={selectedTPPForICT}
                    filterOption={(input: string, option: { label: string }) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Styled.FilterItem>
              </>
            )}

            {/* DATA */}
            {steps[safeCurrent]?.key === STEP_KEYS.DATA && (
              <>
                <Styled.FilterItem>
                  <Styled.FilterLabel>{t('AREAS.ENTITY')}</Styled.FilterLabel>
                  <Styled.SelectFilter
                    allowClear
                    showSearch
                    placeholder={t('AREAS.ENTITY')}
                    options={extractedOptions.map((option) => ({
                      value: option.label,
                      key: option.key,
                      label: option.label,
                    }))}
                    value={query.entity}
                    onChange={handleChange}
                    filterOption={(input: string, option: { label: string }) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Styled.FilterItem>

                <Styled.FilterItem>
                  <Styled.FilterLabel>
                    {t('GENERAL.FUNCTION_SERVICE')}
                  </Styled.FilterLabel>
                  <Styled.SelectFilter
                    allowClear
                    options={[
                      {
                        value: 'ICT_SERVICE',
                        label: 'ICT Service',
                        key: 'ICT_SERVICE',
                      },
                      {
                        value: 'FUNCTION',
                        label: 'Function',
                        key: 'FUNCTION',
                      },
                    ]}
                    value={selectedSystem}
                    onChange={handleSelectSystem}
                    filterOption={(input: string, option: { label: string }) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Styled.FilterItem>
              </>
            )}

            {/* ICT RISK */}
            {steps[safeCurrent]?.key === STEP_KEYS.ICT_RISK && (
              <Styled.FilterItem>
                <Styled.FilterLabel>
                  {t('GENERAL.FUNCTION_SERVICE')}
                </Styled.FilterLabel>
                <Styled.SelectFilter
                  allowClear
                  options={[
                    {
                      value: 'ICT_SERVICE',
                      label: 'ICT Service',
                      key: 'ICT_SERVICE',
                    },
                    {
                      value: 'FUNCTION',
                      label: 'Function',
                      key: 'FUNCTION',
                    },
                  ]}
                  value={selectedSystem}
                  onChange={handleSelectSystem}
                  filterOption={(input: string, option: { label: string }) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Styled.FilterItem>
            )}

            {/* REPORT 99 */}
            {steps[safeCurrent]?.key === STEP_KEYS.REPORT99 && (
              <Styled.FilterItem>
                <Styled.FilterLabel>{t('STEPS.REPORT99')}</Styled.FilterLabel>
                <Styled.SelectFilter
                  allowClear
                  options={options99}
                  value={selectedOptionReport99}
                  onChange={handleChangeReport99}
                  filterOption={(input: string, option: { label: string }) =>
                    option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Styled.FilterItem>
            )}
          </Styled.FilterGrid>
        </Styled.FilterCard>
      )}
      <Styled.ContentArea>
        <Styled.HorizontalLayout theme={scheme}>
          <Styled.StepsComponent token={token}>
            <Styled.StepContent>
              {steps[safeCurrent]?.content ?? null}
              {footer && <div>{footer}</div>}
            </Styled.StepContent>
          </Styled.StepsComponent>
        </Styled.HorizontalLayout>
      </Styled.ContentArea>
    </Styled.RootLayout>
  );
};

export default StepComponent;
